import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { useUI } from '../context/UIContext';
import {
    FiPlus, FiEdit2, FiTrash2, FiEye, FiEyeOff,
    FiAlertTriangle, FiSearch, FiFilter
} from 'react-icons/fi';
import './InventoryPage.css';

const InventoryPage = () => {
    const { isAdmin } = useAuth();
    const { showToast, confirm, isOffline } = useUI();
    const [items, setItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterCategory, setFilterCategory] = useState('');

    const [showHidden, setShowHidden] = useState(true);
    const [showCategoryModal, setShowCategoryModal] = useState(false);
    const [categoryFormData, setCategoryFormData] = useState({ id: null, name: '', description: '' });

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        categoryId: '',
        price: '',
        quantity: '',
        lowStockThreshold: '10',
        image: null,
        isHidden: false,
    });

    useEffect(() => {
        fetchData();
    }, [showHidden]);

    const fetchData = async () => {
        try {
            const [itemsRes, categoriesRes] = await Promise.all([
                api.get(`/inventory?includeHidden=${showHidden}`),
                api.get('/inventory/categories')
            ]);
            setItems(itemsRes.data);
            setCategories(categoriesRes.data);
        } catch (error) {
            console.error('Failed to fetch inventory:', error);
        } finally {
            setLoading(false);
        }
    };

    const filteredItems = items.filter(item => {
        const matchesSearch = !searchQuery ||
            item.name.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesCategory = !filterCategory ||
            item.category_id === parseInt(filterCategory);
        return matchesSearch && matchesCategory;
    });

    const handleOpenModal = (item = null) => {
        if (item) {
            setEditingItem(item);
            setFormData({
                name: item.name,
                description: item.description || '',
                categoryId: item.category_id,
                price: item.price,
                quantity: item.quantity,
                lowStockThreshold: item.low_stock_threshold,
                image: null, // Reset file input
                currentImageUrl: item.image_url,
                isHidden: item.is_hidden,
            });
        } else {
            setEditingItem(null);
            setFormData({
                name: '',
                description: '',
                categoryId: categories[0]?.id || '',
                price: '',
                quantity: '',
                lowStockThreshold: '10',
                image: null,
                currentImageUrl: null,
                isHidden: false,
            });
        }
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const data = new FormData();
            data.append('name', formData.name);
            data.append('description', formData.description);
            data.append('categoryId', formData.categoryId);
            data.append('price', formData.price);
            data.append('quantity', formData.quantity);
            data.append('lowStockThreshold', formData.lowStockThreshold);
            data.append('isHidden', formData.isHidden);
            data.append('isAvailable', true);

            if (formData.image) {
                data.append('image', formData.image);
            }

            if (editingItem) {
                await api.put(`/inventory/${editingItem.id}`, data, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            } else {
                await api.post('/inventory', data, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            }

            setShowModal(false);
            fetchData();
            showToast('Item saved successfully', 'success');
        } catch (error) {
            showToast(error.response?.data?.message || 'Failed to save item', 'error');
        }
    };

    const handleCategorySubmit = async (e) => {
        e.preventDefault();
        try {
            if (categoryFormData.id) {
                await api.put(`/inventory/categories/${categoryFormData.id}`, categoryFormData);
            } else {
                await api.post('/inventory/categories', categoryFormData);
            }
            setShowCategoryModal(false);
            fetchData();
            showToast('Category saved successfully', 'success');
        } catch (error) {
            showToast(error.response?.data?.message || 'Failed to save category', 'error');
        }
    };

    const handleDeleteCategory = async (id) => {
        const confirmed = await confirm('Are you sure? Items in this category must be deleted or moved first.', 'Delete Category');
        if (!confirmed) return;
        try {
            await api.delete(`/inventory/categories/${id}`);
            fetchData();
            showToast('Category deleted successfully', 'success');
        } catch (error) {
            showToast(error.response?.data?.message || 'Failed to delete category', 'error');
        }
    };

    const openCategoryModal = (category = null) => {
        if (category) {
            setCategoryFormData({ id: category.id, name: category.name, description: category.description || '' });
        } else {
            setCategoryFormData({ id: null, name: '', description: '' });
        }
        setShowCategoryModal(true);
    };

    const handleDelete = async (id) => {
        const confirmed = await confirm('Are you sure you want to delete this item?', 'Delete Item');
        if (!confirmed) return;
        try {
            await api.delete(`/inventory/${id}`);
            fetchData();
            showToast('Item deleted successfully', 'success');
        } catch (error) {
            showToast(error.response?.data?.message || 'Failed to delete item', 'error');
        }
    };

    const handleToggleVisibility = async (item) => {
        try {
            await api.patch(`/inventory/${item.id}/visibility`, {
                isHidden: !item.is_hidden
            });
            fetchData();
            showToast('Visibility updated', 'success');
        } catch (error) {
            showToast(error.response?.data?.message || 'Failed to update visibility', 'error');
        }
    };



    if (loading) {
        return (
            <div className="loading-container">
                <div className="spinner spinner-lg"></div>
            </div>
        );
    }

    return (
        <div className="inventory-page">
            <div className="page-header">
                <div>
                    <h1>Inventory Management</h1>
                    <p className="text-muted">Manage your menu items and stock levels</p>
                </div>
                {isAdmin && (
                    <div className="header-actions">
                        <button
                            className="btn btn-secondary"
                            style={{ marginRight: '10px' }}
                            onClick={() => openCategoryModal()}
                            disabled={isOffline}
                            title={isOffline ? "Unavailable offline" : ""}
                        >
                            Manage Categories
                        </button>
                        <button
                            className="btn btn-primary"
                            onClick={() => handleOpenModal()}
                            disabled={isOffline}
                            title={isOffline ? "Unavailable offline" : ""}
                        >
                            <FiPlus /> Add Item
                        </button>
                    </div>
                )}
            </div>

            {/* Filters */}
            <div className="filters-bar">
                <div className="search-box">
                    <FiSearch className="search-icon" />
                    <input
                        type="text"
                        placeholder="Search items..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="form-input"
                    />
                </div>
                <div className="filter-group">
                    <FiFilter />
                    <select
                        className="form-select"
                        value={filterCategory}
                        onChange={(e) => setFilterCategory(e.target.value)}
                    >
                        <option value="">All Categories</option>
                        {categories.map(cat => (
                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                    </select>
                </div>
                <label className="checkbox-label">
                    <input
                        type="checkbox"
                        checked={showHidden}
                        onChange={(e) => setShowHidden(e.target.checked)}
                    />
                    Include hidden items
                </label>
            </div>

            {/* Items Table */}
            <div className="card">
                <div className="table-container">
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Item</th>
                                <th>Category</th>
                                <th>Price</th>
                                <th>Stock</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredItems.map(item => (
                                <tr key={item.id} className={item.is_hidden ? 'hidden-row' : ''}>
                                    <td>
                                        <div className="item-cell">
                                            <div className="item-image">
                                                {item.image_url ? (
                                                    <img src={item.image_url} alt={item.name} />
                                                ) : (
                                                    <span>🍔</span>
                                                )}
                                            </div>
                                            <div>
                                                <div className="item-name">{item.name}</div>
                                                {item.description && (
                                                    <div className="item-desc">{item.description}</div>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                    <td>{item.category_name}</td>
                                    <td className="price-cell">Rs. {parseFloat(item.price).toFixed(2)}</td>
                                    <td>
                                        <div
                                            className={`stock-cell ${item.stockStatus}`}
                                        >
                                            {item.quantity}
                                            {item.stockStatus === 'low_stock' && (
                                                <FiAlertTriangle className="warning-icon" />
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        <span className={`badge badge-${item.is_hidden ? 'gray' :
                                            item.stockStatus === 'out_of_stock' ? 'error' :
                                                item.stockStatus === 'low_stock' ? 'warning' : 'success'
                                            }`}>
                                            {item.is_hidden ? 'Hidden' :
                                                item.stockStatus === 'out_of_stock' ? 'Out of Stock' :
                                                    item.stockStatus === 'low_stock' ? 'Low Stock' : 'Available'}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="action-buttons">
                                            <button
                                                className="btn btn-icon btn-outline"
                                                onClick={() => handleToggleVisibility(item)}
                                                title={item.is_hidden ? 'Show on menu' : 'Hide from menu'}
                                                disabled={isOffline}
                                            >
                                                {item.is_hidden ? <FiEye /> : <FiEyeOff />}
                                            </button>
                                            {isAdmin && (
                                                <>
                                                    <button
                                                        className="btn btn-icon btn-outline"
                                                        onClick={() => handleOpenModal(item)}
                                                        disabled={isOffline}
                                                        title={isOffline ? "Unavailable offline" : ""}
                                                    >
                                                        <FiEdit2 />
                                                    </button>
                                                    <button
                                                        className="btn btn-icon btn-outline btn-danger"
                                                        onClick={() => handleDelete(item.id)}
                                                        disabled={isOffline}
                                                        title={isOffline ? "Unavailable offline" : ""}
                                                    >
                                                        <FiTrash2 />
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {filteredItems.length === 0 && (
                                <tr>
                                    <td colSpan="6" className="text-center text-muted">
                                        No items found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Add/Edit Modal */}
            {showModal && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3 className="modal-title">
                                {editingItem ? 'Edit Item' : 'Add New Item'}
                            </h3>
                            <button className="modal-close" onClick={() => setShowModal(false)}>
                                &times;
                            </button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="modal-body">
                                <div className="form-group">
                                    <label className="form-label">Name *</label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Category *</label>
                                    <select
                                        className="form-select"
                                        value={formData.categoryId}
                                        onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
                                        required
                                    >
                                        {categories.map(cat => (
                                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">Price *</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            className="form-input"
                                            value={formData.price}
                                            onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                                            placeholder="Price (Rs.)"
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Quantity *</label>
                                        <input
                                            type="number"
                                            className="form-input"
                                            value={formData.quantity}
                                            onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Low Stock Threshold</label>
                                    <input
                                        type="number"
                                        className="form-input"
                                        value={formData.lowStockThreshold}
                                        onChange={(e) => setFormData({ ...formData, lowStockThreshold: e.target.value })}
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Description</label>
                                    <textarea
                                        className="form-textarea"
                                        rows="2"
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Image</label>
                                    <input
                                        type="file"
                                        className="form-input"
                                        accept="image/*"
                                        onChange={(e) => setFormData({ ...formData, image: e.target.files[0] })}
                                    />
                                    {editingItem && editingItem.image_url && (
                                        <div className="current-image-preview">
                                            <p className="text-muted text-sm_">Current: {editingItem.image_url.split('/').pop()}</p>
                                        </div>
                                    )}
                                </div>

                                <label className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={formData.isHidden}
                                        onChange={(e) => setFormData({ ...formData, isHidden: e.target.checked })}
                                    />
                                    Hide from menu
                                </label>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    {editingItem ? 'Update' : 'Add'} Item
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Category Modal */}
            {showCategoryModal && (
                <div className="modal-overlay" onClick={() => setShowCategoryModal(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3 className="modal-title">Manage Categories</h3>
                            <button className="modal-close" onClick={() => setShowCategoryModal(false)}>&times;</button>
                        </div>
                        <div className="modal-body">
                            <form onSubmit={handleCategorySubmit} className="category-form">
                                <div className="form-group">
                                    <label className="form-label">Category Name</label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        value={categoryFormData.name}
                                        onChange={(e) => setCategoryFormData({ ...categoryFormData, name: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Description</label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        value={categoryFormData.description}
                                        onChange={(e) => setCategoryFormData({ ...categoryFormData, description: e.target.value })}
                                    />
                                </div>
                                <button type="submit" className="btn btn-primary btn-block">
                                    {categoryFormData.id ? 'Update Category' : 'Add Category'}
                                </button>
                                {categoryFormData.id && (
                                    <button
                                        type="button"
                                        className="btn btn-secondary btn-block"
                                        style={{ marginTop: '10px' }}
                                        onClick={() => setCategoryFormData({ id: null, name: '', description: '' })}
                                    >
                                        Cancel Edit
                                    </button>
                                )}
                            </form>

                            <hr style={{ margin: '20px 0', border: '0', borderTop: '1px solid #eee' }} />

                            <div className="categories-list">
                                <h4>Existing Categories</h4>
                                {categories.map(cat => (
                                    <div key={cat.id} className="category-item-row" style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f5f5f5' }}>
                                        <div>
                                            <strong>{cat.name}</strong>
                                            <p className="text-muted text-sm_">{cat.description}</p>
                                        </div>
                                        <div className="action-buttons">
                                            <button
                                                className="btn btn-icon btn-sm btn-outline"
                                                onClick={() => openCategoryModal(cat)}
                                                disabled={isOffline}
                                            >
                                                <FiEdit2 />
                                            </button>
                                            <button
                                                className="btn btn-icon btn-sm btn-outline btn-danger"
                                                onClick={() => handleDeleteCategory(cat.id)}
                                                disabled={isOffline}
                                            >
                                                <FiTrash2 />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InventoryPage;
